/**
 * Blockgenaues Mitscrollen zwischen Quelltext und Vorschau.
 *
 * Anders als die frühere, rein verhältnisbasierte Fassung (gleicher
 * Scroll-Prozentsatz auf beiden Seiten) hält diese Version bekannte Punkte
 * fest, an denen Quelltext und Vorschau wirklich zusammenfallen: jeder
 * Block, den der Renderer mit seiner Quellzeile beschriftet hat
 * (`data-line`/`data-line-end`, siehe markdown/index.ts), liefert Anfang und
 * Ende. Dazwischen wird linear umgerechnet (die Rechnung dahinter steht ohne
 * DOM in scrollMap.ts, damit sie sich prüfen lässt). Ein Bild oder Diagramm,
 * das eine Zeile Quelltext in vierhundert Pixel Vorschau verwandelt, bleibt
 * damit an Ort und Stelle liegen, statt Nachbarblöcke zu verschieben.
 *
 * Standardmäßig AUS (siehe EditorPage.ts) und pro Notiz gemerkt: zwei
 * ungleich lange Texte laufen trotz der Blockpunkte nie perfekt im
 * Gleichschritt (Bild lädt nach, Diagramm rendert asynchron nach) – wer das
 * nicht will, soll nicht ungefragt mitgezogen werden.
 */

import { buildPoints, project, clamp, type Block, type Point } from "./scrollMap";
import type { CodeEditor } from "./codeEditor";

/** Merkt sich, ob eine Notiz mit verbundenem Scrollen geöffnet werden soll –
 *  dasselbe Muster wie foldPersist.ts, nur ein Bool statt Faltzustände. */
const LINKED_KEY = "lernapp.syncScroll.v1";

function readLinked(): Record<string, boolean> {
  try {
    return JSON.parse(localStorage.getItem(LINKED_KEY) ?? "{}") as Record<string, boolean>;
  } catch {
    return {};
  }
}

export function loadSyncLinked(noteId: string): boolean {
  return readLinked()[noteId] === true;
}

export function saveSyncLinked(noteId: string, linked: boolean): void {
  const all = readLinked();
  if (linked) all[noteId] = true;
  else delete all[noteId];
  try {
    localStorage.setItem(LINKED_KEY, JSON.stringify(all));
  } catch {
    /* Privatmodus o. ä. – Zustand bleibt dann eben nur für die Sitzung. */
  }
}

export interface SyncScroll {
  /** Vorschau auf die Cursorzeile ziehen (z. B. beim Tippen) – nur im verbundenen Modus. */
  followCaret(): void;
  /** Verbunden/getrennt umschalten. */
  setLinked(linked: boolean): void;
  isLinked(): boolean;
  /** Nach einem Vorschau-Update aufrufen – Blockgrenzen können sich verschoben haben. */
  invalidate(): void;
  destroy(): void;
}

function readLine(el: Element, attr: string): number | null {
  const v = el.getAttribute(attr);
  if (v == null) return null;
  const n = parseInt(v, 10);
  return Number.isNaN(n) ? null : n;
}

/**
 * Nur die äußersten Anker (direkte Kinder der Vorschau). Verschachtelte
 * Anker (z. B. `<li>` in `<ul>`) liegen fast an derselben Stelle wie ihr
 * Elternblock – `buildPoints` verwirft solche Beinah-Duplikate ohnehin
 * (strenge Monotonie), das Filtern hier spart nur unnötige Messungen.
 * Zugeklappte Blöcke (previewFold.ts) haben keine Ausdehnung und fliegen raus.
 */
function topLevelAnchors(root: HTMLElement): HTMLElement[] {
  return Array.from(root.children).filter(
    (el): el is HTMLElement =>
      el instanceof HTMLElement &&
      el.hasAttribute("data-line") &&
      !el.classList.contains("md-fold-hidden")
  );
}

function buildBlocks(root: HTMLElement, editor: CodeEditor): Block[] {
  // Bezugspunkt: Oberkante des Vorschau-INHALTS (unabhängig vom aktuellen
  // Scrollstand), damit die Blockpositionen scroll-stabil sind.
  const previewContentTop = root.getBoundingClientRect().top - root.scrollTop;
  const blocks: Block[] = [];
  for (const el of topLevelAnchors(root)) {
    const srcTop0 = readLine(el, "data-line");
    const srcEnd0 = readLine(el, "data-line-end");
    if (srcTop0 == null || srcEnd0 == null || srcEnd0 <= srcTop0) continue;
    const rect = el.getBoundingClientRect();
    blocks.push({
      srcTop: editor.lineTop(srcTop0),
      srcBottom: editor.lineTop(srcEnd0),
      viewTop: rect.top - previewContentTop,
      viewBottom: rect.bottom - previewContentTop,
    });
  }
  return blocks;
}

export function attachSyncScroll(
  editor: CodeEditor,
  view: HTMLElement,
  isActive: () => boolean = () => true
): SyncScroll {
  const src = editor.scroller;
  let linked = false;
  let points: Point[] | null = null;
  /** Verhindert die Rückkopplung „A scrollt B scrollt A". */
  let driver: EventTarget | null = null;
  let releaseTimer: number | undefined;

  const maxScroll = (el: { scrollHeight: number; clientHeight: number }) =>
    Math.max(1, el.scrollHeight - el.clientHeight);

  function ensurePoints(): Point[] {
    if (!points) {
      const blocks = buildBlocks(view, editor);
      points = buildPoints(blocks, maxScroll(src), maxScroll(view));
    }
    return points;
  }

  // Größenänderung der Vorschau (Bild fertig geladen, Diagramm gerendert,
  // Fenster/Schrift geändert) macht die gemessenen Blockgrenzen ungültig.
  const resize = new ResizeObserver(() => {
    points = null;
  });
  resize.observe(view);

  const claim = (who: EventTarget) => {
    driver = who;
    window.clearTimeout(releaseTimer);
    releaseTimer = window.setTimeout(() => (driver = null), 120);
  };

  const onTextScroll = () => {
    if (!linked || !isActive() || driver === view) return;
    claim(src);
    view.scrollTop = clamp(project(ensurePoints(), src.scrollTop, true), 0, maxScroll(view));
  };
  const onViewScroll = () => {
    if (!linked || !isActive() || driver === src) return;
    claim(view);
    src.scrollTop = clamp(project(ensurePoints(), view.scrollTop, false), 0, maxScroll(src));
  };

  src.addEventListener("scroll", onTextScroll, { passive: true });
  view.addEventListener("scroll", onViewScroll, { passive: true });

  return {
    followCaret() {
      if (!linked || !isActive()) return;
      claim(src);
      view.scrollTop = clamp(project(ensurePoints(), src.scrollTop, true), 0, maxScroll(view));
    },
    setLinked(next: boolean) {
      linked = next;
      if (!next) return;
      // Beim Einschalten sofort ausrichten statt erst beim nächsten Scrollen.
      points = null;
      view.scrollTop = clamp(project(ensurePoints(), src.scrollTop, true), 0, maxScroll(view));
    },
    isLinked: () => linked,
    invalidate() {
      points = null;
    },
    destroy() {
      src.removeEventListener("scroll", onTextScroll);
      view.removeEventListener("scroll", onViewScroll);
      resize.disconnect();
      window.clearTimeout(releaseTimer);
    },
  };
}
